import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Mail } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [consent, setConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscountCode, setShowDiscountCode] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const { toast } = useToast();

  const handleNewsletterSignup = async (e) => {
    e.preventDefault();
    
    if (!consent) {
      toast({
        title: "Consent Required",
        description: "Please agree to receive marketing emails.",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/customers/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          firstName,
          lastName,
          marketingConsent: consent,
          source: 'newsletter'
        }),
      });

      if (!response.ok) throw new Error('Failed to subscribe');

      const data = await response.json();
      
      setDiscountCode(data.discount_code);
      setShowDiscountCode(true);
      
      toast({
        title: "Success! 🎉",
        description: data.discount_message || "You've been subscribed to our newsletter!",
        duration: 5000,
      });
      
      setEmail('');
      setFirstName('');
      setLastName('');
      setConsent(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to subscribe. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <footer className="mt-20" style={{ backgroundColor: '#4a2c1a' }}>
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About Section */}
          <div>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#FF8C19' }}>
              The Maple Daddies
            </h3>
            <p className="text-sm mb-4" style={{ color: '#f7f5f2' }}>
              Handcrafted maple syrup from Upstate New York. Made with tradition, passion, and care.
            </p>
            {!showDiscountCode ? (
              <form onSubmit={handleNewsletterSignup} className="space-y-3">
                <p className="text-sm font-semibold" style={{ color: '#f7f5f2' }}>
                  Get 10% Off Your First Order!
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="text"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="bg-white text-sm"
                    style={{ borderColor: '#FF8C19' }}
                  />
                  <Input
                    type="text"
                    placeholder="Last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="bg-white text-sm"
                    style={{ borderColor: '#FF8C19' }}
                  />
                </div>
                <Input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white text-sm"
                  style={{ borderColor: '#FF8C19' }}
                />
                <div className="flex items-start space-x-2">
                  <input
                    type="checkbox"
                    id="consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                    className="mt-1"
                  />
                  <label htmlFor="consent" className="text-xs" style={{ color: '#f7f5f2' }}>
                    I agree to receive promotional emails and newsletters from The Maple Daddies. You can unsubscribe at any time.
                  </label>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white font-semibold"
                  style={{ backgroundColor: '#FF8C19' }}
                >
                  {isSubmitting ? 'Subscribing...' : 'Get 10% Off'}
                </Button>
              </form>
            ) : (
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-sm font-semibold mb-2" style={{ color: '#22c55e' }}>
                  ✅ Subscribed Successfully!
                </p>
                <p className="text-xs mb-2" style={{ color: '#4a2c1a' }}>
                  Your discount code:
                </p>
                <p className="text-2xl font-bold mb-2" style={{ color: '#FF8C19' }}>
                  {discountCode}
                </p>
                <p className="text-xs" style={{ color: '#736c64' }}>
                  Use this code at checkout for 10% off!
                </p>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#FF8C19' }}>
              Quick Links
            </h3>
            <ul className="space-y-2">
              <Link to="/" className="block text-sm hover:text-maple-orange transition-colors" style={{ color: '#f7f5f2' }}>
                Home
              </Link>
              <Link to="/about" className="block text-sm hover:text-maple-orange transition-colors" style={{ color: '#f7f5f2' }}>
                About Us
              </Link>
              <Link to="/shop" className="block text-sm hover:text-maple-orange transition-colors" style={{ color: '#f7f5f2' }}>
                Shop
              </Link>
              <Link to="/faq" className="block text-sm hover:text-maple-orange transition-colors" style={{ color: '#f7f5f2' }}>
                FAQ
              </Link>
              <Link to="/contact" className="block text-sm hover:text-maple-orange transition-colors" style={{ color: '#f7f5f2' }}>
                Contact Us
              </Link>
              <Link to="/track-order" className="block text-sm hover:text-maple-orange transition-colors" style={{ color: '#f7f5f2' }}>
                Track Order
              </Link>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-xl font-bold mb-4" style={{ color: '#FF8C19' }}>
              Contact
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 flex-shrink-0" style={{ color: '#FF8C19' }} />
                <span className="text-sm" style={{ color: '#f7f5f2' }}>
                  Saratoga Springs, NY 12866
                </span>
              </div>
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 flex-shrink-0" style={{ color: '#FF8C19' }} />
                <span className="text-sm" style={{ color: '#f7f5f2' }}>
                  themapledaddies@gmail.com
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t" style={{ borderColor: '#6b4423' }}>
          <div className="flex flex-col items-center space-y-2">
            <p className="text-center text-sm" style={{ color: '#bbb5ae' }}>
              © {new Date().getFullYear()} The Maple Daddies Sugarhouse. All rights reserved.
            </p>
            <p className="text-center text-xs" style={{ color: '#bbb5ae' }}>
              🏳️‍🌈 Proudly LGBTQ+ Owned
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;